/* Customer-side checkout + payment APIs (TanStack Query).
 *
 * Plan §"Payment single graph": all customer-facing payment flows route
 * through the same kiosk graph (CompletePaymentTransactionUseCase / FIO
 * single-poll / ThePay gateway). The customer PWA exposes thin wrappers:
 *
 *   POST /customer-checkout/sessions                      → create session
 *   GET  /customer-checkout/sessions/:id                  → poll session
 *   POST /customer-checkout/sessions/:id/qr-payment       → start QR
 *   POST /customer-checkout/sessions/:id/gateway-payment  → start ThePay
 *   GET  /customer-payments/:paymentId/status             → poll status
 *
 * Endpoints are normalized — actual backend mapping happens in app.ts and
 * customerCheckoutRoutes.ts (when wired); for this scaffold we follow the
 * external contract documented in the plan.
 */

import {
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import type {
  CheckoutFlowOrigin,
  CheckoutSessionDisplayLine,
  CurrencyCode,
  SessionMetadataCheckoutMode,
  SessionMetadataCollect,
} from 'pi-kiosk-shared';
import type { SessionMetadataPromotionsV5 } from './sessionMetadataV5.js';
import { useApiClient } from '../../shared/providers/AppDataProvider.js';
import { useRuntimeConfig } from '../../shared/providers/RuntimeConfigProvider.js';
import { customerQueryKeys } from '../../shared/api/queryKeys.js';
import { useCustomerSession } from '../../shared/session/CustomerSessionProvider.js';
import { useTenantCode } from '../../shared/tenant/TenantProvider.js';
import { hasCustomerApiTenant } from '../../shared/tenant/customerApiTenant.js';
import { isPlatformChromeTenant } from '../../app/platformChromeTenant.js';
import { resolveTenantLegacyApiUrl } from '../../shared/api/resolveApiUrl.js';
import { stormAwareFetch } from '../../shared/http/stormAwareFetch.js';
import { FIO_PAYMENT_STATUS_POLL_MS } from './paymentPollPolicy.js';
import { preferTerminalCheckoutSessionStatus } from './checkoutModeUi.js';
import i18n from '../../i18n.js';
import { resolvePublicApiFailureMessage } from '../../shared/errors/publicApiFailureMessage.js';
import {
  persistCommerceOrderId,
  readCommerceOrderIdFromUnknown,
  readPersistedCommerceOrderId,
} from './persistCommerceOrderId.js';

export type PurposeType = 'PRODUCT' | 'DONATION';
export type FlowType = 'POST_KIOSK' | 'PHONE_FIRST';
export type EntryChannel = 'KIOSK_QR' | 'PHONE_FIRST_PWA' | 'POST_KIOSK_PWA';
export type CheckoutSubMode =
  | 'PAY_NOW_SELF_SERVICE'
  | 'PAY_NOW_STAFF_HANDOFF'
  | 'PREPAY_COLLECT_LATER';

export interface PostKioskTokenResolution {
  paymentId: string;
  flowType: FlowType;
  purposeType: PurposeType;
  donationProjectCode: string | null;
  expectedAmountMinor: number | null;
  currency: string;
  transactionStatus: string;
  refundEligible: boolean;
  /** Concrete SP from resolve — stage guest favourite only when present (Wave F). */
  salesPointId?: number | null;
  /** Optional dual-regime logo-chip rim from resolve wire (G2 auto-stage). */
  showLogoChipRimLight?: boolean;
  showLogoChipRimDark?: boolean;
  logoChipRimColorLight?: string;
  logoChipRimColorDark?: string;
}

export interface CheckoutSessionLineDTO {
  productId: number;
  variantId: number | null;
  quantity: number;
  unitPrice: number;
  lineTotal: number;
}

export interface CheckoutSessionDTO {
  id: string;
  status: 'OPEN' | 'AWAITING_PAYMENT' | 'COMPLETED' | 'EXPIRED' | 'CANCELLED';
  /** When false, mode is locked server-side (payment started or session terminal). */
  checkoutModeEditable?: boolean;
  amountMinor: number;
  currency: string;
  purposeType: PurposeType;
  flowType: FlowType;
  entryChannel: EntryChannel;
  donationProjectCode: string | null;
  /** Non-null when session was created from shop cart (`lines` on create). */
  kioskId: number | null;
  /** Authoritative sales point id from API (G30); may differ from legacy kioskId alias. */
  salesPointId?: number | null;
  /** Line snapshot; empty for amount-only donation sessions. */
  lines: CheckoutSessionLineDTO[];
  checkoutSubMode?: CheckoutSubMode | null;
  collect?: SessionMetadataCollect | null;
  checkoutMode?: SessionMetadataCheckoutMode | null;
  /** G9 — projected from session metadata; SHIPPING skips collect step. */
  fulfillmentMode?: 'PICKUP' | 'SHIPPING' | null;
  /** Server-stamped Delivery Policy summary (no address PII; charge always 0). */
  shipping?: {
    policyId?: number;
    shippingChargeMinor: number;
    linesSubtotalMinor: number;
    serverTotalMinor: number;
    currency: string;
    etaMinBusinessDays?: number | null;
    etaMaxBusinessDays?: number | null;
    latestDeliveryBusinessDays?: number | null;
  } | null;
  /** Present when payment has already been started (AWAITING_PAYMENT). */
  paymentId?: string | null;
  /** Linked order/fulfillment transaction when payment started. */
  transactionId?: number | null;
  /**
   * P5 — ISO expiry when a low-stock InventoryReservation hold is active for this session.
   */
  lowStockReservationExpiresAt?: string | null;
  /**
   * ISO when collect/amount was payment-locked (Self-Scan handoff or payment start).
   * Durable G1 currency-lock SoT — survives stripped `?selfScan=1` (sessionId-only URL).
   */
  collectLockedAt?: string | null;
  /**
   * True when this CCS is bound to a Self-Scan basket (optional).
   * When absent, FE falls back to `collectLockedAt` / residual `basketPublicId`.
   */
  selfScanBound?: boolean | null;
  /** v5 session metadata promotions block (handoff / activate wedge). */
  promotions?: SessionMetadataPromotionsV5;
  /**
   * Seller-Assisted origin (plan §2.3 / §7) — gateway phone read-only review.
   * Prefer these over Self-Scan fields; do not reuse SelfScanBasket.
   */
  checkoutFlowOrigin?: CheckoutFlowOrigin | string | null;
  sellerAssistedBasketPublicId?: string | null;
  /** Tolerate backend mint typo until stamp uses sellerAssistedBasketPublicId. */
  sellerAssistedpublicId?: string | null;
  sellerAssistedBound?: boolean | null;
  basketVersion?: number | null;
  /** Enriched named lines for SA phone review (CheckoutSessionDisplayLine). */
  displayLines?: CheckoutSessionDisplayLine[] | null;
}

export type QrAccess = 'allowed' | 'denied';

export interface QRPaymentResponseDTO {
  paymentId: string;
  /** Legacy SPAYD fallback when server PNG proxy is unavailable. */
  qrCodePayload?: string;
  variableSymbol: string;
  amountMinor: number;
  currency: string;
  qrVisible?: boolean;
  qrAccess?: QrAccess;
  productCollectionMode?: 'PAY_AT_KIOSK' | 'PREPAY_COLLECT_LATER' | null;
  commerceOrderId?: string | null;
}

export interface GatewayPaymentResponseDTO {
  paymentId: string;
  redirectUrl: string;
  amountMinor: number;
  currency: string;
  commerceOrderId?: string | null;
}

export interface CashPaymentStartResponseDTO {
  checkoutSessionId: string;
  salesPointId: number;
  amountMinor: number;
  currency: string;
  transactionId?: number;
  paymentId?: string;
  fulfillmentStatus?: string | null;
  /** When true, payment awaits staff confirmation — client must not call complete. */
  awaitingCashConfirmation?: boolean;
  /** Bank VS when present on the started transaction (G3 pre-first-poll waiting). */
  variableSymbol?: string | null;
  /**
   * Same fulfillment-id label as payment-status poll when VS blank (G3).
   * Never raw transactionId / paymentId / session id.
   */
  allocatedOrderDisplayNumber?: string | null;
  commerceOrderId?: string | null;
}

export interface CashPaymentCompleteResponseDTO {
  transactionId: number;
  paymentId: string;
  fulfillmentStatus: string | null;
  amountMinor: number;
  currency: string;
}

/** Guest receipt recovery payload on completed payment status (Stage 5). */
export interface GuestReceiptRecoveryDTO {
  viewUrl: string;
  downloadUrl?: string;
  variableSymbol?: string | null;
  transactionId?: number | null;
  orderReference?: string | null;
  /**
   * Friendly Crockford access code when issued (plan §5.2 / G10).
   * Coexists with viewUrl/VS — possession proof for FindOrder lookup/link.
   */
  guestAccessCode?: string | null;
}

export interface PaymentStatusDTO {
  paymentId: string;
  status: 'PENDING' | 'COMPLETED' | 'FAILED' | 'EXPIRED' | 'CANCELLED';
  completedAt: string | null;
  /** True while staff has not yet confirmed cash receipt (Stage 2 waiting screen). */
  awaitingCashConfirmation?: boolean;
  qrVisible?: boolean;
  /** True when customer may submit POST /customer-checkout/refund-request. */
  refundEligible?: boolean;
  amountMinor?: number;
  currency?: string;
  checkoutSessionPublicId?: string;
  /** Present when payment is linked to an order/fulfillment transaction. */
  transactionId?: number | null;
  /** Bank transfer variable symbol when a transaction exists. */
  variableSymbol?: string | null;
  /** Customer-facing order reference when allocated (G6 — not session slug). */
  orderReference?: string | null;
  /** Allocated display order number for cash-collect (G6 — friendly ref only). */
  allocatedOrderDisplayNumber?: string | null;
  /** Guest-only recovery QR/link when payment is COMPLETED and a receipt exists. */
  receiptRecovery?: GuestReceiptRecoveryDTO;
}

export interface StartPaymentMonitoringResponseDTO {
  monitoringStartTime: number | null;
}

export function useResolvePostKioskToken(
  token: string | null
): UseQueryResult<PostKioskTokenResolution, Error> {
  const api = useApiClient();
  const tenantCode = useTenantCode();
  return useQuery({
    queryKey: customerQueryKeys.resolvePostKioskToken(tenantCode, token ?? ''),
    queryFn: () =>
      api.request<PostKioskTokenResolution>(
        `/customer-checkout/resolve-token`,
        { method: 'GET', query: { token: token ?? '' } }
      ),
    enabled: token !== null && token.length > 0,
  });
}

/**
 * Shop create / currency-switch recreate lines (ids + qty → catalog price).
 * Self-Scan frozen unitPrice/lineTotal only via CreateCheckoutSessionFromSelfScanBasket;
 * post-handoff currency is locked via `collectLockedAt` / Self-Scan checkout context
 * (`isCheckoutCurrencyLocked`) — not catalog recreate.
 */
export interface CreateSessionLine {
  productId: number;
  variantId?: number | null;
  quantity: number;
}

interface CreateSessionVars {
  /** Optional once `lines` are provided — server derives the amount. */
  amountMinor?: number;
  /** W2: registry CurrencyCode — client may send EUR when SP accepts it. */
  currency: CurrencyCode;
  purposeType: PurposeType;
  flowType: FlowType;
  entryChannel: EntryChannel;
  checkoutSubMode?: CheckoutSubMode;
  /**
   * Donation project selection. Pass an explicit project code to target a
   * specific project, or `null`/omit for the "whichever" choice (resolved
   * server-side per ADR-006).
   */
  donationProjectCode?: string | null;
  postKioskToken?: string;
  /** Required when `lines` is set; scopes catalog and price resolution. */
  kioskId?: number;
  /** API field — set explicitly or derived from `kioskId` on create. */
  salesPointId?: number;
  /** Phase B: server-authoritative cart snapshot. */
  lines?: CreateSessionLine[];
  /** V3 metadata collect selection resolved before checkout creation. */
  collect?: SessionMetadataCollect;
  /** V3 metadata checkout mode selection resolved before checkout creation. */
  checkoutMode?: SessionMetadataCheckoutMode;
  /** G9 — SHIPPING requires shippingLightEnabled + DeliveryPolicy + address (S23). */
  fulfillmentMode?: 'PICKUP' | 'SHIPPING';
  shipping?: {
    shippingChargeMinor?: 0;
    saveAddressToMembership?: boolean;
    address: {
      recipientName: string;
      line1: string;
      line2?: string | null;
      city: string;
      postalCode: string;
      countryCode: string;
      phone?: string | null;
      email?: string | null;
    };
  };
  idempotencyKey?: string;
}

export function useCreateCheckoutSession(): UseMutationResult<
  CheckoutSessionDTO,
  Error,
  CreateSessionVars
> {
  const api = useApiClient();
  const tenantCode = useTenantCode();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ idempotencyKey, kioskId, salesPointId, ...body }) => {
      const resolvedSalesPointId = salesPointId ?? kioskId;
      return api.request<CheckoutSessionDTO>('/customer-checkout/sessions', {
        method: 'POST',
        body: {
          ...body,
          ...(resolvedSalesPointId !== undefined
            ? { salesPointId: resolvedSalesPointId }
            : {}),
        },
        idempotencyKey,
      });
    },
    onSuccess: (data) => {
      queryClient.setQueryData(
        checkoutSessionBaseQueryKey(tenantCode, data.id),
        data
      );
    },
  });
}

interface UpdateCollectVars {
  sessionId: string;
  collect: SessionMetadataCollect;
  checkoutMode?: SessionMetadataCheckoutMode;
}

export function useUpdateCheckoutSessionCollect(): UseMutationResult<
  CheckoutSessionDTO,
  Error,
  UpdateCollectVars
> {
  const api = useApiClient();
  const tenantCode = useTenantCode();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ sessionId, collect, checkoutMode }) =>
      api.request<CheckoutSessionDTO>(
        `/customer-checkout/sessions/${encodeURIComponent(sessionId)}/collect`,
        {
          method: 'PATCH',
          body: { collect, ...(checkoutMode !== undefined ? { checkoutMode } : {}) },
        }
      ),
    onSuccess: (data, { sessionId }) => {
      queryClient.setQueryData(
        checkoutSessionBaseQueryKey(tenantCode, sessionId),
        data
      );
    },
  });
}

/** Query freshness profile for checkout-session GETs. */
export type CheckoutSessionQueryProfile = 'active' | 'complete';

/** Base RQ key — mutations (`create` / `collect`) write here. */
export function checkoutSessionBaseQueryKey(
  tenantCode: string,
  sessionId: string,
): ReturnType<typeof customerQueryKeys.checkoutSession> {
  return customerQueryKeys.checkoutSession(tenantCode, sessionId);
}

/**
 * Live GET key for `useGetCheckoutSession` — profile suffix (`active` | `complete`).
 * Soft remount cold-init must prefer this over the base key.
 */
export function checkoutSessionProfileQueryKey(
  tenantCode: string,
  sessionId: string,
  profile: CheckoutSessionQueryProfile,
): readonly [...ReturnType<typeof customerQueryKeys.checkoutSession>, CheckoutSessionQueryProfile] {
  return [...checkoutSessionBaseQueryKey(tenantCode, sessionId), profile] as const;
}

/**
 * G2 — same cache order as live `useGetCheckoutSession` queryFn:
 * profile (`active` by default) then base. Missing both → `undefined` (defer).
 */
export function readCachedCheckoutSession(
  queryClient: Pick<QueryClient, 'getQueryData'>,
  tenantCode: string,
  sessionId: string,
  profile: CheckoutSessionQueryProfile = 'active',
): CheckoutSessionDTO | undefined {
  const profileKey = checkoutSessionProfileQueryKey(tenantCode, sessionId, profile);
  const baseKey = checkoutSessionBaseQueryKey(tenantCode, sessionId);
  return (
    queryClient.getQueryData<CheckoutSessionDTO>(profileKey) ??
    queryClient.getQueryData<CheckoutSessionDTO>(baseKey)
  );
}

/**
 * Phase B prep — `GET /customer-checkout/sessions/:id`.
 *
 * Returns a snapshot of the server-authoritative session, including the
 * server-derived `amountMinor`. Used by the shop → checkout hand-off.
 * Disabled when no `sessionId` is present.
 *
 * - `'active'`: live checkout (mild staleTime / always refetch on window focus so
 *   Seller-Assisted staff edits / basketVersion bumps are visible after tab return).
 * - `'complete'`: post-pay return surface — long staleTime, no mount/focus refetch
 *   to reduce session spam on the complete route.
 */
export function useGetCheckoutSession(
  sessionId: string | null,
  profile: CheckoutSessionQueryProfile = 'active',
): UseQueryResult<CheckoutSessionDTO, Error> {
  const api = useApiClient();
  const tenantCode = useTenantCode();
  const queryClient = useQueryClient();
  const isCompleteProfile = profile === 'complete';
  return useQuery({
    queryKey: checkoutSessionProfileQueryKey(tenantCode, sessionId ?? '', profile),
    queryFn: async () => {
      const fetched = await api.request<CheckoutSessionDTO>(
        `/customer-checkout/sessions/${encodeURIComponent(sessionId ?? '')}`,
      );
      const cached = readCachedCheckoutSession(
        queryClient,
        tenantCode,
        sessionId ?? '',
        profile,
      );
      return preferTerminalCheckoutSessionStatus(cached, fetched);
    },
    enabled: sessionId !== null && sessionId.length > 0,
    staleTime: isCompleteProfile ? 300_000 : 10_000,
    refetchOnMount: isCompleteProfile ? false : true,
    // Boolean `true` skips refetch while data is still within staleTime (10s) —
    // that missed G17 SA basketVersion bumps on quick focus return.
    refetchOnWindowFocus: isCompleteProfile ? false : 'always',
  });
}

interface StartPaymentVars {
  sessionId: string;
  /** Behavioral analytics session UUID (bound via `bindSessionId` before pay). */
  analyticsSessionId: string;
  /**
   * Customer email used to find-or-create the platform Customer in the kiosk
   * payment pipeline. Optional in the type because authenticated flows may
   * resolve the email server-side from the linked Customer record; the FE
   * checkout screen prompts for it whenever it is missing.
   */
  customerEmail?: string;
  idempotencyKey?: string;
  gatewayProviderId?: string;
  activatedCouponId?: string | null;
  activatedPromoRewardId?: string | null;
}

export function useStartQrPayment(): UseMutationResult<
  QRPaymentResponseDTO,
  Error,
  StartPaymentVars
> {
  const api = useApiClient();
  const tenantCode = useTenantCode();
  const { session } = useCustomerSession();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      sessionId,
      analyticsSessionId,
      customerEmail,
      idempotencyKey,
      activatedCouponId,
      activatedPromoRewardId,
    }) => {
      const persistedOrderId = readPersistedCommerceOrderId(sessionId);
      const data = await api.request<QRPaymentResponseDTO>(
        `/customer-checkout/sessions/${encodeURIComponent(sessionId)}/qr-payment`,
        {
          method: 'POST',
          body: {
            analyticsSessionId,
            ...(customerEmail !== undefined ? { customerEmail } : {}),
            ...(activatedCouponId !== undefined ? { activatedCouponId } : {}),
            ...(activatedPromoRewardId !== undefined ? { activatedPromoRewardId } : {}),
            language: i18n.resolvedLanguage ?? i18n.language,
            ...(persistedOrderId !== undefined ? { commerceOrderId: persistedOrderId } : {}),
          },
          idempotencyKey,
        }
      );
      const orderId = readCommerceOrderIdFromUnknown(data);
      if (orderId !== undefined) {
        persistCommerceOrderId(sessionId, orderId);
      }
      return data;
    },
    onSuccess: () => {
      // G5: payment start stamps Default uiLocale — refetch so settings do not
      // stay stuck on receiptLocaleSyncPending when the server already has live locale.
      const customerId = session?.customerId;
      if (customerId === undefined) {
        return;
      }
      void queryClient.invalidateQueries({
        queryKey: customerQueryKeys.accountReceiptLocales(tenantCode, customerId),
      });
    },
  });
}

interface CompleteCashPaymentVars extends StartPaymentVars {
  amountMinor: number;
}

export function useStartCashPayment(): UseMutationResult<
  CashPaymentStartResponseDTO,
  Error,
  StartPaymentVars
> {
  const api = useApiClient();
  const tenantCode = useTenantCode();
  const { session } = useCustomerSession();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      sessionId,
      analyticsSessionId,
      idempotencyKey,
      activatedCouponId,
      activatedPromoRewardId,
    }) => {
      const persistedOrderId = readPersistedCommerceOrderId(sessionId);
      const data = await api.request<CashPaymentStartResponseDTO>(
        `/customer-checkout/sessions/${encodeURIComponent(sessionId)}/cash-payment`,
        {
          method: 'POST',
          body: {
            analyticsSessionId,
            ...(activatedCouponId !== undefined ? { activatedCouponId } : {}),
            ...(activatedPromoRewardId !== undefined ? { activatedPromoRewardId } : {}),
            language: i18n.resolvedLanguage ?? i18n.language,
            ...(persistedOrderId !== undefined ? { commerceOrderId: persistedOrderId } : {}),
          },
          idempotencyKey,
        },
      );
      const orderId = readCommerceOrderIdFromUnknown(data);
      if (orderId !== undefined) {
        persistCommerceOrderId(sessionId, orderId);
      }
      return data;
    },
    onSuccess: () => {
      const customerId = session?.customerId;
      if (customerId === undefined) {
        return;
      }
      void queryClient.invalidateQueries({
        queryKey: customerQueryKeys.accountReceiptLocales(tenantCode, customerId),
      });
    },
  });
}

/**
 * @deprecated Customer cash checkout uses start response only (awaiting staff confirmation).
 * Do not chain complete in the phone-first cash flow.
 */
export function useCompleteCashPayment(): UseMutationResult<
  CashPaymentCompleteResponseDTO,
  Error,
  CompleteCashPaymentVars
> {
  const api = useApiClient();
  return useMutation({
    mutationFn: async ({
      sessionId,
      analyticsSessionId,
      amountMinor,
      idempotencyKey,
      activatedCouponId,
      activatedPromoRewardId,
    }) => {
      return api.request<CashPaymentCompleteResponseDTO>(
        `/customer-checkout/sessions/${encodeURIComponent(sessionId)}/cash-payment/complete`,
        {
          method: 'POST',
          body: {
            analyticsSessionId,
            amountMinor,
            ...(activatedCouponId !== undefined ? { activatedCouponId } : {}),
            ...(activatedPromoRewardId !== undefined ? { activatedPromoRewardId } : {}),
            language: i18n.resolvedLanguage ?? i18n.language,
          },
          idempotencyKey,
        },
      );
    },
  });
}

export function useStartGatewayPayment(): UseMutationResult<
  GatewayPaymentResponseDTO,
  Error,
  StartPaymentVars
> {
  const api = useApiClient();
  const tenantCode = useTenantCode();
  const { session } = useCustomerSession();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      sessionId,
      analyticsSessionId,
      customerEmail,
      idempotencyKey,
      gatewayProviderId,
      activatedCouponId,
      activatedPromoRewardId,
    }) => {
      const persistedOrderId = readPersistedCommerceOrderId(sessionId);
      const data = await api.request<GatewayPaymentResponseDTO>(
        `/customer-checkout/sessions/${encodeURIComponent(sessionId)}/gateway-payment`,
        {
          method: 'POST',
          body: {
            analyticsSessionId,
            ...(customerEmail !== undefined ? { customerEmail } : {}),
            ...(gatewayProviderId !== undefined ? { gatewayProviderId } : {}),
            ...(activatedCouponId !== undefined ? { activatedCouponId } : {}),
            ...(activatedPromoRewardId !== undefined ? { activatedPromoRewardId } : {}),
            language: i18n.resolvedLanguage ?? i18n.language,
            ...(persistedOrderId !== undefined ? { commerceOrderId: persistedOrderId } : {}),
          },
          idempotencyKey,
        }
      );
      const orderId = readCommerceOrderIdFromUnknown(data);
      if (orderId !== undefined) {
        persistCommerceOrderId(sessionId, orderId);
      }
      return data;
    },
    onSuccess: () => {
      // G5: payment start stamps Default uiLocale — refetch so settings do not
      // stay stuck on receiptLocaleSyncPending when the server already has live locale.
      const customerId = session?.customerId;
      if (customerId === undefined) {
        return;
      }
      void queryClient.invalidateQueries({
        queryKey: customerQueryKeys.accountReceiptLocales(tenantCode, customerId),
      });
    },
  });
}

/**
 * Keep polling while PENDING, or while COMPLETED until guest `receiptRecovery`
 * arrives (bank-transfer confirmation QR). Stop on FAILED/EXPIRED/CANCELLED or
 * COMPLETED with recovery present. Cap ALWAYS bounds COMPLETED-without-recovery:
 * prefer valid `completedAt`, else first COMPLETED-seen clock (no infinite poll).
 */
export const RECEIPT_RECOVERY_POLL_CAP_MS = 120_000;

/** First COMPLETED-without-recovery observation when `completedAt` is unusable. */
const completedWithoutRecoverySeenAtMs = new Map<string, number>();

export function resetCompletedWithoutRecoveryPollClockForTests(): void {
  completedWithoutRecoverySeenAtMs.clear();
}

function resolveCompletedWithoutRecoveryCapAnchorMs(
  data: PaymentStatusDTO,
  cacheKey: string,
  nowMs: number
): number {
  if (data.completedAt != null && data.completedAt.length > 0) {
    const completedMs = Date.parse(data.completedAt);
    if (Number.isFinite(completedMs)) {
      completedWithoutRecoverySeenAtMs.delete(cacheKey);
      return completedMs;
    }
  }
  const existing = completedWithoutRecoverySeenAtMs.get(cacheKey);
  if (existing !== undefined) {
    return existing;
  }
  completedWithoutRecoverySeenAtMs.set(cacheKey, nowMs);
  return nowMs;
}

export interface PaymentStatusPollIntervalOptions {
  /** Stable key for first-COMPLETED clock (queryHash or paymentId). */
  cacheKey?: string;
  /** Injectable clock for tests. */
  nowMs?: number;
}

/**
 * Poll interval policy for payment status queries.
 * Exported for focused G2 unit tests (cap / missing completedAt).
 */
export function paymentStatusPollInterval(
  data: PaymentStatusDTO | undefined,
  pendingPollMs: number,
  options?: PaymentStatusPollIntervalOptions
): number | false {
  if (data === undefined) {
    return pendingPollMs;
  }
  if (data.status === 'PENDING') {
    return pendingPollMs;
  }
  const cacheKey =
    options?.cacheKey?.trim() ||
    (data.paymentId.length > 0 ? data.paymentId : '');
  if (data.status === 'COMPLETED' && data.receiptRecovery == null) {
    const nowMs = options?.nowMs ?? Date.now();
    const key = cacheKey.length > 0 ? cacheKey : '__anonymous__';
    const anchorMs = resolveCompletedWithoutRecoveryCapAnchorMs(data, key, nowMs);
    if (nowMs - anchorMs >= RECEIPT_RECOVERY_POLL_CAP_MS) {
      return false;
    }
    return pendingPollMs;
  }
  if (cacheKey.length > 0) {
    completedWithoutRecoverySeenAtMs.delete(cacheKey);
  }
  return false;
}

interface PaymentStatusQueryOptions {
  enabled?: boolean;
  /** Row-tenant override when chrome is `__platform__` (e.g. account QR `?tenant=`). */
  tenantCode?: string;
}

export function usePaymentStatus(
  paymentId: string | null,
  pendingPollMs = FIO_PAYMENT_STATUS_POLL_MS,
  options?: PaymentStatusQueryOptions
): UseQueryResult<PaymentStatusDTO, Error> {
  const api = useApiClient();
  const tenantCode = useTenantCode();
  const apiTenant = options?.tenantCode?.trim() || tenantCode;
  const idPresent = paymentId !== null && paymentId.length > 0;
  const enabled =
    (options?.enabled ?? true) && idPresent && hasCustomerApiTenant(apiTenant);
  return useQuery({
    queryKey: customerQueryKeys.paymentStatus(apiTenant, paymentId ?? ''),
    queryFn: () =>
      api.request<PaymentStatusDTO>(
        `/customer-payments/${encodeURIComponent(paymentId ?? '')}/status`,
        { tenantCode: apiTenant }
      ),
    enabled,
    // Override customerQueryClient defaults (staleTime 30s / refetchOnWindowFocus false):
    // bank-app tab return must always be eligible to see COMPLETED after SSE miss.
    staleTime: 0,
    refetchOnWindowFocus: true,
    refetchInterval: (q) =>
      paymentStatusPollInterval(q.state.data, pendingPollMs, {
        cacheKey: q.queryHash,
      }),
    refetchIntervalInBackground: false,
  });
}

export function usePaymentStatusByCheckoutSession(
  sessionId: string | null,
  options?: PaymentStatusQueryOptions
): UseQueryResult<PaymentStatusDTO, Error> {
  const api = useApiClient();
  const tenantCode = useTenantCode();
  const apiTenant = options?.tenantCode?.trim() || tenantCode;
  const idPresent = sessionId !== null && sessionId.length > 0;
  const enabled =
    (options?.enabled ?? true) && idPresent && hasCustomerApiTenant(apiTenant);
  return useQuery({
    queryKey: customerQueryKeys.paymentStatus(apiTenant, `cs:${sessionId ?? ''}`),
    queryFn: () =>
      api.request<PaymentStatusDTO>(
        `/customer-payments/by-checkout-session/${encodeURIComponent(sessionId ?? '')}/status`,
        { tenantCode: apiTenant }
      ),
    enabled,
    staleTime: 0,
    refetchOnWindowFocus: true,
    refetchInterval: (q) =>
      paymentStatusPollInterval(q.state.data, FIO_PAYMENT_STATUS_POLL_MS, {
        cacheKey: q.queryHash,
      }),
    refetchIntervalInBackground: false,
  });
}

interface PaymentMonitoringVars {
  paymentId: string;
  tenantCode?: string;
}

async function postMonitoring<T>(
  url: string,
  body: Record<string, unknown>
): Promise<T> {
  const response = await stormAwareFetch(url, {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
    credentials: 'include',
  });
  const payload = (await response.json().catch(() => null)) as
    | { success?: boolean; data?: T; error?: string }
    | null;
  if (!response.ok || payload?.success !== true || payload.data === undefined) {
    throw new Error(
      resolvePublicApiFailureMessage({
        status: response.status,
        rawMessage: payload?.error,
      }),
    );
  }
  return payload.data;
}

function resolvePaymentMonitoringTenant(
  chromeTenantCode: string,
  overrideTenantCode: string | undefined
): string {
  const monitorTenant = (overrideTenantCode?.trim() || chromeTenantCode).trim();
  if (isPlatformChromeTenant(monitorTenant)) {
    throw new Error(
      i18n.t('errors.openShopToContinue', { ns: 'customer' }),
    );
  }
  return monitorTenant;
}

export function useStartPaymentMonitoring(): UseMutationResult<
  StartPaymentMonitoringResponseDTO,
  Error,
  PaymentMonitoringVars
> {
  const { apiBaseUrl } = useRuntimeConfig();
  const tenantCode = useTenantCode();
  return useMutation({
    mutationFn: (vars) => {
      const monitorTenant = resolvePaymentMonitoringTenant(tenantCode, vars.tenantCode);
      return postMonitoring<StartPaymentMonitoringResponseDTO>(
        resolveTenantLegacyApiUrl(apiBaseUrl, monitorTenant, '/payments/start-monitoring'),
        { paymentId: vars.paymentId }
      );
    },
  });
}

export function useStopPaymentMonitoring(): UseMutationResult<
  { success: boolean },
  Error,
  PaymentMonitoringVars
> {
  const { apiBaseUrl } = useRuntimeConfig();
  const tenantCode = useTenantCode();
  return useMutation({
    mutationFn: (vars) => {
      const monitorTenant = resolvePaymentMonitoringTenant(tenantCode, vars.tenantCode);
      return postMonitoring<{ success: boolean }>(
        resolveTenantLegacyApiUrl(apiBaseUrl, monitorTenant, '/payments/stop-monitoring'),
        { paymentId: vars.paymentId }
      );
    },
  });
}

export interface CustomerRefundRequestResult {
  paymentId: string;
  transactionId: number;
  refundStatus: 'REQUESTED';
  idempotent: boolean;
}

export function useSubmitCustomerRefundRequest(): UseMutationResult<
  CustomerRefundRequestResult,
  Error,
  { paymentId: string; tenantCode?: string; idempotencyKey?: string }
> {
  const api = useApiClient();
  return useMutation({
    mutationFn: async ({ paymentId, tenantCode: overrideTenantCode, idempotencyKey }) => {
      // Live contract: POST body stays `{ paymentId }` only. Idempotency-Key is
      // optional on this ONE path (inferred when absent). Do not send refundRequestKind.
      // Row-tenant override required under platform chrome (Account QR `?tenant=`).
      const trimmedKey = idempotencyKey?.trim() ?? '';
      return api.request<CustomerRefundRequestResult>('/customer-checkout/refund-request', {
        method: 'POST',
        body: { paymentId },
        idempotency: 'optional',
        ...(trimmedKey.length > 0 ? { idempotencyKey: trimmedKey } : {}),
        ...(overrideTenantCode !== undefined && overrideTenantCode.trim().length > 0
          ? { tenantCode: overrideTenantCode.trim() }
          : {}),
      });
    },
  });
}

export interface CustomerRecoveryHandoffResult {
  paymentId: string;
  token: string;
  expiresAt: string;
  customerFrontendUrl: string;
}

export function useIssueCustomerRecoveryHandoff(): UseMutationResult<
  CustomerRecoveryHandoffResult,
  Error,
  { paymentId: string }
> {
  const api = useApiClient();
  return useMutation({
    mutationFn: ({ paymentId }) =>
      api.request<CustomerRecoveryHandoffResult>('/customer-checkout/recovery-handoff', {
        method: 'POST',
        body: { paymentId },
      }),
  });
}
